import { flatten, without } from 'ramda';
import * as React from 'react';

import { useHotelsEnabled } from '../../../../../../../shared/common/utils/experiments';
import { FOfferType, isAvailableOfferTypeCombination } from '../../../../../../JsonQuery';
import { getOfferTypeLabel } from '../../../../utils/offerType';
import { getOfferTypeList } from '../helpers';
import { IOfferTypeFilterProps } from '../types';

export function useOfferTypeFilter({ dealType, value, onChange: onChaneProp }: IOfferTypeFilterProps) {
  const isHotelsEnabled = useHotelsEnabled();
  const offerTypes = React.useMemo(() => getOfferTypeList(dealType, value, isHotelsEnabled), [dealType, value]);
  const offerTypeLabel = React.useMemo(() => getOfferTypeLabel(dealType, value), [dealType, value]);

  const selectedOptions = React.useMemo(
    () =>
      flatten(offerTypes)
        .filter(offerType => (value & offerType.value) !== 0)
        .map(offerType => offerType.value),
    [offerTypes, value],
  );

  const onChange = React.useCallback(
    (_: never, selectedValues: FOfferType[]) => {
      const selectedOfferType = without(
        selectedOptions.length < selectedValues.length ? selectedOptions : selectedValues,
        selectedOptions.length < selectedValues.length ? selectedValues : selectedOptions,
      )[0];

      if (selectedOfferType === value) {
        return;
      }

      const nextOfferType: FOfferType = selectedValues.reduce(
        (previousValue, currentValue) => previousValue | currentValue,
        FOfferType.Unexpected,
      );

      if (selectedOptions.length > selectedValues.length) {
        onChaneProp(nextOfferType);
      } else {
        if (isAvailableOfferTypeCombination(nextOfferType)) {
          onChaneProp(nextOfferType);
        } else {
          onChaneProp(selectedOfferType);
        }
      }
    },
    [offerTypes, value],
  );

  return React.useMemo(
    () => ({
      offerTypes,
      offerTypeLabel,
      selectedOptions,
      onChange,
    }),
    [offerTypes, offerTypeLabel, selectedOptions, onChange],
  );
}
